import { Dialog, Disclosure, Popover, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { environment } from 'environment';
import { DateTime } from 'luxon';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Fragment, useEffect, useRef, useState } from 'react';
import { classes, scrubString } from 'shared';
import { Button } from '../../general/button';
import { A } from '../../general/link';
import { useWindowSize } from '../../hooks/useWindowSize';
import { LogoMainLong, LogoMainLongWhite } from '../../img/logo';
import { InvertBlueArch, InvertWhiteArch } from '../../img/svgs';
import { useNavContext } from './context';

const NavbarDynamic = dynamic(() => import('./navbar-dynamic').then(mod => mod.NavbarDynamic), { ssr: false });

const navigation = [
  { name: 'home', href: '/', level: '0' },
  { name: 'who we are', href: '/who-we-are', level: '0' },
  { name: 'where donations go', href: '/where-donations-go', level: '4' },
  { name: 'FAQs', href: '/faqs', level: '4' },
  { name: 'contact us', href: '/contact', level: '4' },
  { name: 'how to help', href: '/how-to-help', level: '0' },
  { name: 'donate furniture', href: '/furniture', level: '4' },
  { name: 'donate money', href: '/donate', level: '4' },
  {
    name: 'wishlist (walmart)',
    href: 'https://www.walmart.com/registry/RR/9eab970c-fef8-4888-b1ea-b40ea3cb372e',
    level: '4',
    external: true,
  },
  { name: 'partners & donors', href: '/donors', level: '0' },
  { name: 'corporate partners', href: '/partners', level: '4' },
  { name: 'bulk furniture partners', href: '/furniture-partners', level: '4' },
  { name: 'community donors', href: '/donors', level: '4' },
  { name: 'get furniture', href: '/get-furniture', level: '0' },
  { name: 'how we work', href: '/how-we-work', level: '4' },
  { name: 'referral agencies', href: '/agencies', level: '4' },
  { name: 'news & events', href: '/news', level: '0', noProd: false },
];

export function NavBar() {
  const { blueTopArch } = useNavContext();
  const currentPath = useRouter().pathname;
  const router = useRouter();
  const home = router.pathname === '/';
  const is15thAnniversary = router.pathname === '/15';

  // const textWhite = home || blueTopArch;
  const textWhite = blueTopArch;

  const now = DateTime.now();
  const showDoubleHeightBanner = (now.month === 12 && now.day >= 17) || (now.month === 1 && now.day < 5);

  if (is15thAnniversary) return null;

  return (
    // <Disclosure as="nav" className="bg-white fixed w-full z-10">
    <Disclosure
      as="nav"
      className={classes(
        'z-50',
        !home
          ? 'relative'
          : showDoubleHeightBanner
            ? `absolute mt-[var(--banner-double-height)] w-full`
            : `absolute mt-[var(--banner-height)] w-full`,
        !textWhite && !home && 'bg-white',
        blueTopArch && 'bg-blue-500',
      )}>
      {({ open, close }) => (
        <>
          {!home && !blueTopArch ? (
            <InvertWhiteArch className="absolute left-0 right-0 top-0 z-[1] pt-[calc(var(--banner-height)+6px)] lg:pt-0" />
          ) : null}

          {!!blueTopArch && (
            <InvertBlueArch className="absolute left-0 right-0 top-0 z-[1] pt-[calc(var(--banner-height)+6px)] lg:pt-0" />
          )}
          <div className="z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 lg:py-4">
            <div className="z-10 flex h-16 justify-between">
              <div className="z-10 flex">
                <div className="-ml-2 mr-2 flex items-center lg:hidden">
                  {/* Mobile menu button */}
                  <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                    <span className="sr-only">open main menu</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
                <div className="flex flex-shrink-0 items-center">
                  <Link href="/">
                    {textWhite ? (
                      <LogoMainLongWhite className="h-10 lg:h-16" />
                    ) : (
                      <LogoMainLong className="h-10 lg:h-16" />
                    )}
                  </Link>
                </div>
                <div className="z-10 hidden lg:ml-6 lg:flex lg:space-x-8">
                  {whoWeArePopover()}
                  {howToHelpPopover()}
                  {partnersAndDonorsPopover()}
                  {getFurniturePopover()}
                  <div className="flex items-center justify-center">
                    <A
                      href="/news"
                      className={classes(
                        'group inline-flex items-center rounded-md text-base font-medium text-blue-500 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-4',
                        textWhite && 'text-white hover:text-gray-100 focus:ring-0 focus:ring-offset-0',
                      )}>
                      news &amp; events
                    </A>
                  </div>
                </div>
              </div>
              <div className="z-10 flex items-center">
                <NavbarDynamic textWhite={textWhite} />
                <div className="flex-shrink-0">
                  <Button
                    bType={textWhite ? 'secondary-white' : home ? 'secondary-outline' : 'quaternary'}
                    link="/donate"
                    className="!px-6">
                    donate now
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile */}
          <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 z-50 flex lg:hidden" onClose={() => close()}>
              <Transition.Child
                as={Fragment}
                enter="transition-opacity ease-linear duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0">
                <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
              </Transition.Child>
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full">
                <div className="relative flex w-full max-w-xs flex-1 flex-col bg-white pb-4 pt-5">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="absolute right-0 top-0 -mr-12 pt-2">
                      <button
                        type="button"
                        className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                        onClick={() => close()}>
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                      </button>
                    </div>
                  </Transition.Child>
                  <div className="flex flex-shrink-0 items-center px-4">
                    <Link href={'/'}>
                      <LogoMainLong className="h-10" />
                    </Link>
                    <h2 className="ml-2 text-2xl">Beds for Kids</h2>
                  </div>
                  <div className="mt-5 h-0 flex-1 overflow-y-auto">
                    <nav className="space-y-1 px-2">
                      {navigation.map(item => {
                        if (item.noProd && environment.name === 'production') return null;
                        return (
                          <Link
                            href={item.href}
                            key={`${scrubString(item.name)}-${item.level}`}
                            onClick={() => close()}
                            target={item.external ? '_blank' : undefined}
                            className={classes(
                              calcActive(item.href)
                                ? 'bg-gray-100 text-gray-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                              'group flex items-center rounded-md px-2 py-2 text-base font-medium',
                              item.level && `ml-${item.level}`,
                            )}>
                            {/* <item.icon
                              className={classes(
                                hCurrRoute(item.href) ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500',
                                'mr-4 h-6 w-6 flex-shrink-0',
                              )}
                              aria-hidden="true"
                            /> */}
                            {item.name}
                          </Link>
                        );
                      })}
                      <NavbarDynamic textWhite={textWhite} mobile calcActive={calcActive} />
                    </nav>
                  </div>
                </div>
              </Transition.Child>
              <div className="w-14 flex-shrink-0" aria-hidden="true">
                {/* Dummy element to force sidebar to shrink to fit close icon */}
              </div>
            </Dialog>
          </Transition.Root>
        </>
      )}
    </Disclosure>
  );

  function calcActive(path: string) {
    if (path === '/' && !currentPath.replaceAll('/', '')) return true;
    if (path === currentPath) return true;
    return false;
  }

  function whoWeArePopover() {
    return (
      <Popover className="relative flex items-center justify-center">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={classes(
                open ? (textWhite ? 'text-white' : 'text-blue-400') : textWhite ? 'text-gray-50' : 'text-blue-500',
                'group inline-flex items-center rounded-md text-base font-medium hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                textWhite && 'hover:text-gray-100 focus:ring-0 focus:ring-offset-0',
              )}>
              <span>who we are</span>
              <ChevronDownIcon
                className={classes(
                  open ? (textWhite ? 'text-white' : 'text-blue-400') : textWhite ? 'text-gray-50' : 'text-blue-500',
                  'ml-1 mt-1 h-5 w-5 group-hover:text-blue-500',
                  textWhite && 'group-hover:text-gray-100',
                )}
                aria-hidden="true"
              />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1">
              <Popover.Panel className="absolute left-0 top-16 z-10 w-screen max-w-xs transform px-2 text-blue-500">
                <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="relative grid gap-2 bg-white p-4">
                    <DesktopDropdownLink close={close} href="/who-we-are" text="who we are" />
                    <DesktopDropdownLink close={close} href="/where-donations-go" text="where donations go" />
                    <DesktopDropdownLink close={close} href="/faqs" text="FAQs" />
                    <DesktopDropdownLink close={close} href="/contact" text="contact" />
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  }

  function howToHelpPopover() {
    return (
      <Popover className="relative flex items-center justify-center">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={classes(
                open ? (textWhite ? 'text-white' : 'text-blue-400') : textWhite ? 'text-gray-50' : 'text-blue-500',
                'group inline-flex items-center rounded-md text-base font-medium hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-4',
                textWhite && 'hover:text-gray-100 focus:ring-0 focus:ring-offset-0',
              )}>
              <span>how to help</span>
              <ChevronDownIcon
                className={classes(
                  open ? (textWhite ? 'text-white' : 'text-blue-400') : textWhite ? 'text-gray-50' : 'text-blue-500',
                  'ml-1 mt-1 h-5 w-5 group-hover:text-blue-500',
                  textWhite && 'group-hover:text-gray-100',
                )}
                aria-hidden="true"
              />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1">
              <Popover.Panel className="absolute left-0 top-16 z-10 w-screen max-w-xs transform px-2 text-blue-500 sm:px-0">
                <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="relative grid gap-2 bg-white p-4">
                    <DesktopDropdownLink close={close} href="/how-to-help" text="how to help" />
                    <DesktopDropdownLink close={close} href="/how-to-help?tab=volunteer" text="donate time" />
                    <DesktopDropdownLink close={close} href="/furniture" text="donate furniture" />
                    <DesktopDropdownLink close={close} href="/donate" text="donate money" />
                    <DesktopDropdownLink
                      external
                      close={close}
                      href="https://www.walmart.com/registry/RR/9eab970c-fef8-4888-b1ea-b40ea3cb372e"
                      text="wishlist (walmart)"
                    />
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  }

  function partnersAndDonorsPopover() {
    return (
      <Popover className="relative flex items-center justify-center">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={classes(
                open ? (textWhite ? 'text-white' : 'text-blue-400') : textWhite ? 'text-gray-50' : 'text-blue-500',
                'group inline-flex items-center rounded-md text-base font-medium hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-4',
                textWhite && 'hover:text-gray-100 focus:ring-0 focus:ring-offset-0',
              )}>
              <span>partners & donors</span>
              <ChevronDownIcon
                className={classes(
                  open ? (textWhite ? 'text-white' : 'text-blue-400') : textWhite ? 'text-gray-50' : 'text-blue-500',
                  'ml-1 mt-1 h-5 w-5 group-hover:text-blue-500',
                  textWhite && 'group-hover:text-gray-100',
                )}
                aria-hidden="true"
              />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1">
              <Popover.Panel className="absolute left-0 top-16 z-10 w-screen max-w-xs transform px-2 text-blue-500 sm:px-0">
                <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="relative grid gap-2 bg-white p-4">
                    <DesktopDropdownLink close={close} href="/donors" text="community donors" />
                    <DesktopDropdownLink close={close} href="/partners" text="corporate partners" />
                    <DesktopDropdownLink close={close} href="/furniture-partners" text="bulk furniture partners" />
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  }

  function getFurniturePopover() {
    return (
      <Popover className="relative flex items-center justify-center">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={classes(
                open ? (textWhite ? 'text-white' : 'text-blue-400') : textWhite ? 'text-gray-50' : 'text-blue-500',
                'group inline-flex items-center rounded-md text-base font-medium hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-4',
                textWhite && 'hover:text-gray-100 focus:ring-0 focus:ring-offset-0',
              )}>
              <span>get furniture</span>
              <ChevronDownIcon
                className={classes(
                  open ? (textWhite ? 'text-white' : 'text-blue-400') : textWhite ? 'text-gray-50' : 'text-blue-500',
                  'ml-1 mt-1 h-5 w-5 group-hover:text-blue-500',
                  textWhite && 'group-hover:text-gray-100',
                )}
                aria-hidden="true"
              />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1">
              <Popover.Panel className="absolute left-0 top-16 z-10 w-screen max-w-xs transform px-2 text-blue-500 sm:px-0">
                <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="relative grid gap-2 bg-white p-4">
                    <DesktopDropdownLink close={close} href="/how-we-work" text="how we work" />
                    <DesktopDropdownLink close={close} href="/agencies" text="referring agencies" />
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  }

  function DesktopDropdownLink({
    external,
    close,
    href,
    text,
  }: {
    external?: boolean;
    close: () => void;
    href: string;
    text: string;
  }) {
    return (
      <A
        onClick={() => close()}
        href={href}
        external={external}
        className={classes(
          'flex items-start rounded-lg p-2 transition duration-150 ease-in-out',
          calcActive(href) ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
        )}>
        <div className="ml-4">
          <p className="text-base font-medium">{text}</p>
        </div>
      </A>
    );
  }
}

export function HeaderDivider() {
  const ref = useRef<SVGSVGElement>(null);
  const [offset, setOffset] = useState(0);
  const window = useWindowSize();

  useEffect(() => {
    if (!ref.current) return;
    const divider = ref.current;
    const dividerHeight = divider.getBoundingClientRect().height;
    console.warn(dividerHeight);
    setOffset(dividerHeight);
  }, [ref, window]);

  return (
    <div className="pointer-events-none absolute left-0 right-0 z-0" style={{ bottom: `-${offset}px` }}>
      <InvertWhiteArch dRef={ref} />
    </div>
  );
}
